import { useEffect, useState, useCallback } from 'react';
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { AppShell, ColorScheme, ColorSchemeProvider, MantineProvider } from '@mantine/core';
import { Router, ReactLocation, Route, Outlet } from "@tanstack/react-location";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Screens from "./screens";
import ApiKeys from './constants/ApiKeys';
import { AuthNavigation } from './navigation';
import * as AppModel from "./model/App";
import { Navbar as AppNavbar } from "./components";

function App() {
    // Track user authentication...
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [ignoreAuthStateChange, setIgnoreAuthStateChange] = useState<boolean>(false);

    // Theming and navbar...
    const [colorScheme, setColorScheme] = useState<ColorScheme>('light');
    const toggleColorScheme = (value?: ColorScheme) => setColorScheme(value || (colorScheme === 'dark' ? 'light' : 'dark'));

    // Initialize Firebase app...
    if (!firebase.apps.length) { firebase.initializeApp(ApiKeys.FirebaseConfig); }

    // Listen for firebase auth changes and update authenticated status...
    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => { 
            if (ignoreAuthStateChange) { return; }  // Don't set state if we're ignoring this event      
            setIsAuthenticated(!!user); 
        });
        return () => unsubscribe(); // Cleanup subscription on unmount (https://usehooks.com/useAuth/)
    }, [ignoreAuthStateChange]);

    // Set document title...
    useEffect(() => { document.title = "Aarón Estrada Legal : Patent & Trademark Docket"; })

    // Occurs when another page instructs App to ignore auth state change events...
    const onIgnoreAuthStateChange = useCallback((ignore: boolean) => { 
        setIgnoreAuthStateChange(ignore); 
        if (!ignore) { setIsAuthenticated(AppModel.Firebase.Auth.isUserLoggedIn()); }
    }, [setIgnoreAuthStateChange, setIsAuthenticated]);

    // When "Logout" is pressed...
    const onLogout = useCallback(() => {
        AppModel.Firebase.Auth.signOut().then(() => {
            window.location.reload();
        }); 
    }, []);

    // Location and routes...
    const location = new ReactLocation();
    const routes: Route[] = [
        { path: "/", element: <Screens.TrademarksScreen /> },
        { path: "/test", element: <Screens.TestScreen /> },
        { path: "/trademarks", element: <Screens.TrademarksScreen /> },
        { path: "/patents", element: <Screens.PatentsScreen /> },
        { path: "/trademark/:id", element: <Screens.TrademarkScreen /> },
        { path: "/patent/:id", element: <Screens.PatentScreen /> },
        { path: "/client/:id", element: <Screens.ClientScreen /> },
        { path: "/clients", element: <Screens.ClientsScreen /> },
        { path: "/statistics", element: <Screens.StatisticsScreen /> },
        { path: "/trademarkDocket", element: <Screens.TrademarkDocketScreen /> },
        { path: "/patentDocket", element: <Screens.PatentDocketScreen /> },
        { path: "/settings", element: <Screens.SettingsScreen /> }
    ];

    // Query...
    const queryClient = new QueryClient();

    // Render navigation based on authentication state...    
    return (isAuthenticated)
        ? 
        <Router location={location} routes={routes}>
            <QueryClientProvider client={queryClient}>
                <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
                        <MantineProvider theme={{ colorScheme, primaryColor: "red", primaryShade: 9 }} withGlobalStyles>
                            <AppShell navbarOffsetBreakpoint="sm" fixed 
                                navbar={<div style={{ marginRight: "80px" }}><AppNavbar onLogout={onLogout} /></div>}
                                footer={<div style={{ marginLeft: "90px", fontSize: "12px" }}>v.20221130.001</div>}
                            >
                                <Outlet />
                            </AppShell>
                        </MantineProvider>
                </ColorSchemeProvider>
            </QueryClientProvider>
        </Router>
        : 
        <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
            <MantineProvider theme={{ colorScheme, primaryColor: "red", primaryShade: 9 }} withGlobalStyles>
                <AuthNavigation onIgnoreAuthStateChange={onIgnoreAuthStateChange} />
            </MantineProvider>
        </ColorSchemeProvider>
}

export default App;
